<template>
  <div class="data-table-wrap">
    <div class="data-filters">
      <div class="filter-row">
        <div class="filter-group">
          <span class="filter-label">Day</span>
          <div class="day-pills">
            <button :class="['day-pill', { active: selectedDays === null }]" @click="selectedDays = null">All</button>
            <button
              v-for="day in ALL_DAYS"
              :key="day"
              :class="['day-pill', { active: selectedDays !== null && selectedDays.includes(day), faded: selectedDays !== null && !selectedDays.includes(day) }]"
              :style="pillStyle(day)"
              @click="toggleDay(day)"
            >{{ day.slice(0, 3) }}</button>
          </div>
        </div>
        <template v-if="filteredTypes.length > 1">
        <div class="filter-divider"></div>
        <div class="filter-group">
          <span class="filter-label">Type</span>
          <div class="day-pills">
            <button :class="['day-pill', { active: selectedType === null }]" @click="selectedType = null">All</button>
            <button
              v-for="t in filteredTypes"
              :key="t.value"
              :class="['day-pill', { active: selectedType === t.value }]"
              @click="selectedType = selectedType === t.value ? null : t.value"
            >{{ t.label }}</button>
          </div>
        </div>
        </template>
        <div class="filter-divider"></div>
        <div class="filter-group">
          <span class="filter-label">Date</span>
          <div class="date-filters">
            <div class="date-shortcuts">
              <button @click="setDateRange('2026')">2026</button>
              <button @click="setDateRange('2025')">2025</button>
              <button @click="setDateRange('12mo')">Last 12 mo</button>
              <button @click="setDateRange('all')">All</button>
            </div>
            <label>From <input type="date" v-model="dateFrom" @change="fetchPage(1)" /></label>
            <label>To <input type="date" v-model="dateTo" @change="fetchPage(1)" /></label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="table-loading">
      <i class="fas fa-spinner fa-spin"></i> Loading...
    </div>
    <div v-else-if="error" class="table-error">{{ error }}</div>
    <template v-else>
      <div class="table-scroll">
        <table class="solves-table">
          <thead>
            <tr>
              <th class="col-status"></th>
              <th :class="['sortable', sortClass('pd')]" @click="setSort('pd')">Date <i class="fas fa-sort"></i></th>
              <th v-if="showTypeColumn">Type</th>
              <th>Day</th>
              <th :class="['sortable', sortClass('t')]" @click="setSort('t')">Solve Time <i class="fas fa-sort"></i></th>
              <th :class="['sortable', sortClass('at')]" @click="setSort('at')">Adj Time <i class="fas fa-sort"></i></th>
              <th :class="['sortable', 'hide-on-small', sortClass('c')]" @click="setSort('c')">Checks <i class="fas fa-sort"></i></th>
              <th :class="['sortable', 'hide-on-small', sortClass('r')]" @click="setSort('r')">Reveals <i class="fas fa-sort"></i></th>
              <th :class="['sortable', 'hide-on-small', sortClass('ca')]" @click="setSort('ca')">Completed At <i class="fas fa-sort"></i></th>
              <th class="hide-on-small">Constructor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="solves.length === 0">
              <td colspan="9" class="empty-row">No solves found</td>
            </tr>
            <tr
              v-for="s in solves"
              :key="s.pd"
              :class="{ 'row-excluded': s.e }"
              @click="goToPuzzle(s)"
            >
              <td class="col-status">
                <i v-if="s.e" class="fas fa-star status-excluded"></i>
                <i v-else-if="s.s" class="fas fa-star status-solved"></i>
                <i v-else class="fas fa-star status-completed"></i>
              </td>
              <td class="col-date">{{ formatDate(s.pd) }}</td>
              <td v-if="showTypeColumn"><span class="type-badge" :class="'type-' + s.pt">{{ s.pt }}</span></td>
              <td><span class="dow-badge" :style="dowStyle(s.dow)">{{ dowFull(s.dow).slice(0, 3) }}</span></td>
              <td>{{ prettyTime(s.t) }}</td>
              <td>{{ prettyTime(s.at) }}</td>
              <td class="hide-on-small">{{ s.c }}</td>
              <td class="hide-on-small">{{ s.r }}</td>
              <td class="hide-on-small">{{ formatDatetime(s.ca) }}</td>
              <td class="hide-on-small col-constructor">{{ s.co }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-pagination">
        <span class="pagination-info">{{ paginationInfo }}</span>
        <div class="pagination-controls">
          <button :disabled="page <= 1" @click="fetchPage(1)"><i class="fas fa-angle-double-left"></i></button>
          <button :disabled="page <= 1" @click="fetchPage(page - 1)"><i class="fas fa-angle-left"></i></button>
          <span class="page-label">{{ page }} / {{ pages }}</span>
          <button :disabled="page >= pages" @click="fetchPage(page + 1)"><i class="fas fa-angle-right"></i></button>
          <button :disabled="page >= pages" @click="fetchPage(pages)"><i class="fas fa-angle-double-right"></i></button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const axios = require('axios');
import { format, parseISO } from 'date-fns';
import { SHORT_DAY_MAPPING } from '../utils/dates';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ALL_TYPES = [
  { value: 'daily', label: 'Daily' },
  { value: 'mini',  label: 'Mini' },
  { value: 'midi',  label: 'Midi' },
];
const DAY_COLORS = {
  Sunday:    '#e05252',
  Monday:    '#4a90d9',
  Tuesday:   '#3aac7a',
  Wednesday: '#e09c3a',
  Thursday:  '#9b6dcc',
  Friday:    '#d96a3a',
  Saturday:  '#3aaccc',
};

export default {
  name: 'SolveDataTable',
  props: {
    initialType: { type: String, default: null },
    availableTypes: { type: Array, default: () => ALL_TYPES.map(t => t.value) },
  },
  data() {
    return {
      ALL_DAYS,
      ALL_TYPES,
      solves: [],
      loading: true,
      error: null,
      page: 1,
      pages: 1,
      total: 0,
      perPage: 100,
      sort: 'pd',
      dir: 'desc',
      selectedDays: null,
      selectedType: this.initialType,
      dateFrom: '',
      dateTo: '',
    };
  },
  computed: {
    filteredTypes() {
      return ALL_TYPES.filter(t => this.availableTypes.includes(t.value));
    },
    showTypeColumn() {
      return this.filteredTypes.length > 1 && this.selectedType === null;
    },
    paginationInfo() {
      if (this.total === 0) return 'No results';
      const start = (this.page - 1) * this.perPage + 1;
      const end = Math.min(this.page * this.perPage, this.total);
      return `${start}–${end} of ${this.total}`;
    },
  },
  watch: {
    selectedDays() { this.fetchPage(1); },
    selectedType() { this.fetchPage(1); },
  },
  mounted() {
    this.fetchPage(1);
  },
  methods: {
    setDateRange(preset) {
      const today = new Date();
      const pad = n => String(n).padStart(2, '0');
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      if (preset === '2026') {
        this.dateFrom = '2026-01-01';
        this.dateTo = '2026-12-31';
      } else if (preset === '2025') {
        this.dateFrom = '2025-01-01';
        this.dateTo = '2025-12-31';
      } else if (preset === '12mo') {
        const from = new Date(today);
        from.setFullYear(from.getFullYear() - 1);
        this.dateFrom = fmt(from);
        this.dateTo = fmt(today);
      } else {
        this.dateFrom = '';
        this.dateTo = '';
      }
      this.fetchPage(1);
    },
    fetchPage(p) {
      this.loading = true;
      this.error = null;
      const params = { page: p, sort: this.sort, dir: this.dir };
      if (this.selectedDays) params.dow = this.selectedDays.join(',');
      if (this.selectedType) params.type = this.selectedType;
      if (this.dateFrom) params.date_from = this.dateFrom;
      if (this.dateTo) params.date_to = this.dateTo;
      axios.get('/api/solves', { params }).then(resp => {
        this.solves = resp.data.solves;
        this.page = resp.data.page;
        this.pages = resp.data.pages;
        this.total = resp.data.total;
        this.perPage = resp.data.per_page;
      }).catch(() => {
        this.error = 'Failed to load solves.';
      }).finally(() => {
        this.loading = false;
      });
    },
    setSort(field) {
      if (this.sort === field) {
        this.dir = this.dir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort = field;
        this.dir = field === 'pd' ? 'desc' : 'asc';
      }
      this.fetchPage(1);
    },
    sortClass(field) {
      if (this.sort !== field) return '';
      return this.dir === 'asc' ? 'sort-asc' : 'sort-desc';
    },
    toggleDay(day) {
      if (this.selectedDays === null) {
        this.selectedDays = [day];
      } else if (this.selectedDays.includes(day)) {
        const remaining = this.selectedDays.filter(d => d !== day);
        this.selectedDays = remaining.length ? remaining : null;
      } else {
        this.selectedDays = [...this.selectedDays, day];
      }
    },
    pillStyle(day) {
      const color = DAY_COLORS[day];
      if (this.selectedDays !== null && this.selectedDays.includes(day)) {
        return { background: color, borderColor: color, color: '#fff' };
      }
      return { borderColor: color, color: color };
    },
    dowFull(dow) {
      return SHORT_DAY_MAPPING[dow] || dow;
    },
    dowStyle(dow) {
      const color = DAY_COLORS[this.dowFull(dow)] || '#999';
      return { color, borderColor: color };
    },
    formatDate(iso) {
      return format(parseISO(iso), 'MMM d, yyyy');
    },
    formatDatetime(iso) {
      if (!iso) return '—';
      return format(parseISO(iso), 'MMM d, yyyy h:mm a');
    },
    prettyTime(secs) {
      if (secs == null || secs < 0) return '—';
      const m = Math.floor(secs / 60);
      const s = Math.floor(secs % 60);
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
    goToPuzzle(s) {
      const url = s.pt === 'daily' ? `/puzzles/${s.pd}` : `/puzzles/${s.pt}/${s.pd}`;
      window.location.href = url;
    },
  },
};
</script>
